import React, { useState, useEffect } from "react";
import { CheckCircle2, Quote, PenLine, Sparkles } from "lucide-react";
import { Textarea } from "../components/ui/textarea";
import BreathworkTimer from "./BreathworkTimer";

const DailyResetCard = ({ reset, doneToday, onMarkDone }) => {
  const [journalIn, setJournalIn] = useState("");
  const [journalOut, setJournalOut] = useState("");

  useEffect(() => {
    // Clear journal answers when reset content changes (e.g., Pro state changes)
    setJournalIn("");
    setJournalOut("");
  }, [reset?.breathwork?.name, reset?.category]);

  const handleMarkDone = () => {
    onMarkDone({ journalIn, journalOut });
  };

  return (
    <section className="px-6 pt-2 pb-6 space-y-5 animate-fade-up" data-testid="daily-reset-card">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[#234B38]">Today's Reset</p>
          <h2 className="text-3xl font-medium text-[#1A2E24] mt-1 leading-tight" style={{ fontFamily: "Outfit, sans-serif" }}>
            {reset.category}
          </h2>
        </div>
        <span className="rounded-full bg-[#F5D0D6]/60 text-[#234B38] px-3 py-1 text-[10px] font-semibold uppercase tracking-wider">
          3 min
        </span>
      </div>

      <BreathworkTimer
        pattern={reset.breathwork.pattern}
        cycles={reset.breathwork.cycles}
        name={reset.breathwork.name}
        instruction={reset.breathwork.instruction}
      />

      {/* Mindset reframe */}
      <div className="rounded-3xl bg-white border border-[#E2EBE6] p-6 shadow-[0_8px_32px_rgba(35,75,56,0.06)]" data-testid="mindset-card">
        <div className="flex items-center gap-2 text-[#234B38]">
          <Quote className="w-4 h-4" strokeWidth={1.75} />
          <span className="text-xs font-semibold uppercase tracking-[0.2em]">Mindset Reframe</span>
        </div>
        <p className="mt-3 text-lg leading-relaxed text-[#1A2E24] text-balance" style={{ fontFamily: "Outfit, sans-serif" }}>
          "{reset.mindset}"
        </p>
      </div>

      {/* Journaling */}
      <div className="rounded-3xl bg-white border border-[#E2EBE6] p-6 shadow-[0_8px_32px_rgba(35,75,56,0.06)]" data-testid="journal-card">
        <div className="flex items-center gap-2 text-[#234B38]">
          <PenLine className="w-4 h-4" strokeWidth={1.75} />
          <span className="text-xs font-semibold uppercase tracking-[0.2em]">Journal — In & Out</span>
        </div>
        <div className="mt-4 space-y-4">
          <div>
            <label className="text-[11px] font-semibold uppercase tracking-wider text-[#234B38]">In</label>
            <p className="text-sm text-[#1A2E24] mt-1 mb-2">{reset.journalIn}</p>
            <Textarea
              data-testid="journal-in-input"
              value={journalIn}
              onChange={(e) => setJournalIn(e.target.value)}
              placeholder="A line is enough..."
              className="rounded-2xl border-[#E2EBE6] bg-[#FDFBF7] focus-visible:ring-[#234B38] resize-none min-h-[64px]"
            />
          </div>
          <div>
            <label className="text-[11px] font-semibold uppercase tracking-wider text-[#234B38]">Out</label>
            <p className="text-sm text-[#1A2E24] mt-1 mb-2">{reset.journalOut}</p>
            <Textarea
              data-testid="journal-out-input"
              value={journalOut}
              onChange={(e) => setJournalOut(e.target.value)}
              placeholder="Set it down here..."
              className="rounded-2xl border-[#E2EBE6] bg-[#FDFBF7] focus-visible:ring-[#234B38] resize-none min-h-[64px]"
            />
          </div>
        </div>
      </div>

      {/* Affirmation */}
      <div className="rounded-3xl bg-gradient-to-br from-[#F5D0D6]/40 to-[#FDFBF7] border border-[#F5D0D6]/60 p-6 shadow-[0_8px_32px_rgba(35,75,56,0.06)]" data-testid="affirmation-card">
        <div className="flex items-center gap-2 text-[#234B38]">
          <Sparkles className="w-4 h-4" strokeWidth={1.75} />
          <span className="text-xs font-semibold uppercase tracking-[0.2em]">Affirmation</span>
        </div>
        <p className="mt-3 text-xl leading-snug text-[#1A2E24] text-balance" style={{ fontFamily: "Outfit, sans-serif" }}>
          {reset.affirmation}
        </p>
      </div>

      <button
        onClick={handleMarkDone}
        disabled={doneToday}
        data-testid="mark-done-btn"
        className={`w-full rounded-full py-4 font-medium flex items-center justify-center gap-2 transition-all shadow-md no-tap-highlight ${
          doneToday
            ? "bg-[#E2EBE6] text-[#5C7568] cursor-not-allowed"
            : "bg-[#234B38] hover:bg-[#1A382A] active:scale-[0.98] text-white"
        }`}
      >
        <CheckCircle2 className="w-5 h-5" strokeWidth={1.75} />
        {doneToday ? "Completed today" : "Mark as done"}
      </button>
    </section>
  );
};

export default DailyResetCard;
